import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Users, Check, X, Calendar, QrCode } from 'lucide-react';
import { toast } from 'sonner';

// HELPER 1: Pega a data de hoje cravada no fuso local (impede que pule para amanhã à noite)
const getLocalDateString = () => {
  const today = new Date();
  const localOffset = today.getTimezoneOffset() * 60000;
  const localDate = new Date(today.getTime() - localOffset);
  return localDate.toISOString().split('T')[0];
};

// HELPER 2: Quebra a string "YYYY-MM-DD" do banco e monta a data isolada, imune a fuso horário
const formatDinnerDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString; // Fallback caso venha em outro formato
  const [year, month, day] = parts;
  // No JS, o mês começa em 0 (Janeiro = 0), por isso o month - 1
  const dateObj = new Date(year, month - 1, day);
  return dateObj.toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const Dinners = () => {
  const [dinners, setDinners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [churrasqueiros, setChurrasqueiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [formData, setFormData] = useState({
    date: getLocalDateString(), // Puxando o Helper 1
    total_cost: '',
    notes: '',
    churrasqueiro_id: '',
    churrasqueiro_name: '',
    churrasqueiro_pix: '',
    churrasqueiro_qrcode: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dinnersRes, customersRes, churrasqueirosRes] = await Promise.all([
        api.dinners.getAll(),
        api.customers.getAll(),
        api.churrasqueiros.getAll()
      ]);
      setDinners(dinnersRes.data);
      setCustomers(customersRes.data);
      setChurrasqueiros(churrasqueirosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (customer) => {
    const exists = selectedParticipants.find(p => p.customer_id === customer.id);
    if (exists) {
      setSelectedParticipants(selectedParticipants.filter(p => p.customer_id !== customer.id));
    } else {
      setSelectedParticipants([
        ...selectedParticipants,
        {
          customer_id: customer.id,
          name: customer.name,
          phone: customer.phone,
          cpf_cnpj: customer.cpf_cnpj,
          paid: false
        }
      ]);
    }
  };

  const handleChurrasqueiroChange = (value) => {
    const selected = churrasqueiros.find(c => c.id === value);
    if (selected) {
      setFormData({
        ...formData,
        churrasqueiro_id: selected.id,
        churrasqueiro_name: selected.name,
        churrasqueiro_pix: selected.pix_key,
        churrasqueiro_qrcode: selected.pix_qrcode_url
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedParticipants.length === 0) {
      toast.error('Adicione pelo menos um participante');
      return;
    }

    try {
      // Correção 1: Calculando a matemática exata antes de enviar ao banco
      const dinnerData = {
        ...formData,
        total_cost: parseFloat(formData.total_cost),
        participants: selectedParticipants,
        cost_per_person: parseFloat(formData.total_cost) / selectedParticipants.length
      };
      
      await api.dinners.create(dinnerData);
      toast.success('Janta criada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar janta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta janta?')) return;
    try {
      await api.dinners.delete(id);
      toast.success('Janta excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir janta');
    }
  };

  const markAsPaid = async (dinnerId, participantIndex) => {
    try {
      await api.dinners.markPaid(dinnerId, participantIndex);
      toast.success('Pagamento marcado como pago!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao marcar pagamento');
    }
  };

  const resetForm = () => {
    setFormData({
      date: getLocalDateString(), // Puxando o Helper 1
      total_cost: '',
      notes: '',
      churrasqueiro_id: '',
      churrasqueiro_name: '',
      churrasqueiro_pix: '',
      churrasqueiro_qrcode: ''
    });
    setSelectedParticipants([]);
  };

  const calculateStats = (dinner) => {
    const paid = dinner.participants.filter(p => p.paid).length;
    const pending = dinner.participants.length - paid;
    return { paid, pending, total: dinner.participants.length };
  };

  return (
    <div className="space-y-6" data-testid="dinners-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Jantas</h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase" data-testid="add-dinner-button">
              <Plus className="mr-2 h-5 w-5" /> Nova Janta
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dinner-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase font-bold">Nova Janta</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Data</Label>
                  <Input
                    type="date"
                    data-testid="dinner-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Valor Total (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="dinner-cost-input"
                    value={formData.total_cost}
                    onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Selecionar Churrasqueiro</Label>
                <Select onValueChange={handleChurrasqueiroChange}>
                  <SelectTrigger className="mt-1 font-mono">
                    <SelectValue placeholder="Selecione um churrasqueiro cadastrado" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {churrasqueiros.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nome do Churrasqueiro</Label>
                  <Input
                    data-testid="churrasqueiro-name-input"
                    value={formData.churrasqueiro_name}
                    onChange={(e) => setFormData({ ...formData, churrasqueiro_name: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">PIX do Churrasqueiro</Label>
                  <Input
                    data-testid="churrasqueiro-pix-input"
                    value={formData.churrasqueiro_pix}
                    onChange={(e) => setFormData({ ...formData, churrasqueiro_pix: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="CPF, telefone ou email"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Observações</Label>
                <Input
                  data-testid="dinner-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1 font-mono"
                  placeholder="Opcional"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider mb-2">Participantes ({selectedParticipants.length})</Label>
                <div className="border border-border p-4 max-h-60 overflow-y-auto space-y-2">
                  {customers.map((customer) => (
                    <div key={customer.id} className="flex items-center gap-3 p-2 hover:bg-secondary/50">
                      <Checkbox
                        checked={selectedParticipants.some(p => p.customer_id === customer.id)}
                        onCheckedChange={() => toggleParticipant(customer)}
                        data-testid={`participant-${customer.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{customer.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {customer.phone || customer.cpf_cnpj || 'Sem contato'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedParticipants.length > 0 && formData.total_cost && (
                <div className="p-4 bg-primary/10 border-2 border-primary">
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Valor por pessoa</p>
                  <p className="text-3xl font-mono font-bold text-primary">
                    R$ {(parseFloat(formData.total_cost) / selectedParticipants.length).toFixed(2)}
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase h-12" data-testid="dinner-submit-button">
                Criar Janta
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {dinners.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhuma janta registrada</p>
            </CardContent>
          </Card>
        ) : (
          dinners.map((dinner) => {
            const stats = calculateStats(dinner);
            
            // Correção 2: Cálculo dinâmico para resgatar as jantas corrompidas do passado
            const dynamicCostPerPerson = dinner.cost_per_person 
              ? dinner.cost_per_person 
              : (dinner.total_cost / (dinner.participants?.length || 1));

            return (
              <Card key={dinner.id} className="bg-card border-border" data-testid={`dinner-card-${dinner.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Calendar className="h-6 w-6 text-primary" />
                      <div>
                        <CardTitle className="text-xl font-bold">
                          {formatDinnerDate(dinner.date)}
                        </CardTitle>
                        {dinner.notes && <p className="text-sm text-muted-foreground">{dinner.notes}</p>}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(dinner.id)}
                      className="hover:bg-destructive/10 hover:text-destructive"
                      data-testid={`delete-dinner-${dinner.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-secondary border border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Custo Total</p>
                      <p className="text-2xl font-mono font-bold text-foreground">R$ {(dinner.total_cost || 0).toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-secondary border border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Por Pessoa</p>
                      <p className="text-2xl font-mono font-bold text-primary">R$ {dynamicCostPerPerson.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-secondary border border-border">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pagamentos</p>
                      <p className="text-2xl font-mono font-bold">
                        <span className="text-primary">{stats.paid}</span>
                        <span className="text-muted-foreground"> / </span>
                        <span className="text-foreground">{stats.total}</span>
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-primary/5 border border-primary/20 mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Churrasqueiro</p>
                      <p className="font-bold text-foreground text-lg">{dinner.churrasqueiro_name}</p>
                      <p className="text-sm font-mono text-primary">PIX: {dinner.churrasqueiro_pix}</p>
                    </div>
                    {dinner.churrasqueiro_qrcode && (
                      <div className="flex-shrink-0 bg-white p-2 rounded h-32 w-32 flex items-center justify-center">
                        <img 
                          src={dinner.churrasqueiro_qrcode} 
                          alt="QR Code" 
                          className="w-full h-full object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Participantes
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nome</TableHead>
                          <TableHead className="text-muted-foreground font-mono uppercase text-xs">Contato/PIX</TableHead>
                          <TableHead className="text-muted-foreground font-mono uppercase text-xs">Valor</TableHead>
                          <TableHead className="text-muted-foreground font-mono uppercase text-xs">Status</TableHead>
                          <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dinner.participants.map((participant, index) => (
                          <TableRow key={index} className="border-border/50">
                            <TableCell className="font-medium">{participant.name}</TableCell>
                            <TableCell className="font-mono text-sm">{participant.phone || participant.cpf_cnpj || '-'}</TableCell>
                            <TableCell className="font-mono font-bold text-primary">R$ {dynamicCostPerPerson.toFixed(2)}</TableCell>
                            <TableCell>
                              {participant.paid ? (
                                <span className="flex items-center gap-1 text-primary font-bold uppercase text-xs">
                                  <Check className="h-4 w-4" /> Pago
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-destructive font-bold uppercase text-xs">
                                  <X className="h-4 w-4" /> Pendente
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {!participant.paid && (
                                <Button
                                  size="sm"
                                  onClick={() => markAsPaid(dinner.id, index)}
                                  className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-bold uppercase"
                                  data-testid={`mark-paid-${dinner.id}-${index}`}
                                >
                                  Marcar Pago
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Dinners;
